import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './Button';
import { Activity, AlertCircle, CheckCircle2 } from 'lucide-react';
interface QuizResultsProps {
  score: number;
}
export function QuizResults({
  score
}: QuizResultsProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const duration = 3000;
    const interval = 50;
    const steps = duration / interval;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      setProgress(Math.min(currentStep / steps * 100, 100));
      if (currentStep >= steps) {
        clearInterval(timer);
        setTimeout(() => setIsAnalyzing(false), 400);
      }
    }, interval);
    return () => clearInterval(timer);
  }, []);
  // Calculate phase based on score (higher score = worse phase)
  // Max possible score is around 12-14 depending on options
  const phase = score > 8 ? 'III (Avanzada)' : score > 4 ? 'II (Intermedia)' : 'I (Inicial)';
  if (isAnalyzing) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="max-w-md mx-auto text-center px-4 py-20" data-id="element-43">
        <div className="relative w-32 h-32 mx-auto mb-8" data-id="element-44">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-0 rounded-full border-4 border-cream-200 border-t-burgundy-600" data-id="element-45" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-46">
            <Activity className="w-10 h-10 text-burgundy-600 animate-pulse" data-id="element-47" />
          </div>
        </div>

        <h2 className="text-2xl font-serif text-burgundy-800 mb-4" data-id="element-48">
          Analizando tus respuestas...
        </h2>

        <div className="h-2 w-full bg-cream-200 rounded-full overflow-hidden mb-4" data-id="element-49">
          <div className="h-full bg-burgundy-600 transition-all duration-75 ease-linear" style={{
          width: `${progress}%`
        }} data-id="element-50" />
        </div>

        <div className="space-y-2 text-sm text-slate-500 font-medium" data-id="element-51">
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: progress > 20 ? 1 : 0
        }} data-id="element-52">
            Evaluando tensión muscular...
          </motion.p>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: progress > 60 ? 1 : 0
        }} data-id="element-53">
            Calculando edad metabólica facial...
          </motion.p>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: progress > 90 ? 1 : 0
        }} data-id="element-54">
            Generando protocolo personalizado...
          </motion.p>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.6
  }} className="max-w-2xl mx-auto px-4 py-12" data-id="element-55">
      <div className="bg-white rounded-3xl shadow-premium border border-cream-200 overflow-hidden" data-id="element-56">
        <div className="bg-burgundy-800 text-white p-8 text-center relative overflow-hidden" data-id="element-57">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-58"></div>
          <AlertCircle className="w-12 h-12 text-gold-400 mx-auto mb-4 relative z-10" data-id="element-59" />
          <h2 className="text-3xl md:text-4xl font-serif mb-2 relative z-10" data-id="element-60">
            Diagnóstico Preliminar
          </h2>
          <p className="text-burgundy-100 text-lg relative z-10" data-id="element-61">
            Atrofia Muscular Detectada: Fase {phase}
          </p>
        </div>

        <div className="p-8 md:p-10" data-id="element-62">
          <div className="flex items-start gap-4 mb-8 bg-cream-100 p-6 rounded-2xl" data-id="element-63">
            <div className="mt-1" data-id="element-64">
              <CheckCircle2 className="w-6 h-6 text-burgundy-600" data-id="element-65" />
            </div>
            <p className="text-lg text-slate-700 leading-relaxed" data-id="element-66">
              Esto significa que el colágeno no está llegando a las capas
              profundas porque{' '}
              <strong className="text-burgundy-800" data-id="element-67">
                tus músculos están inactivos
              </strong>
              . Las cremas superficiales no podrán revertir este proceso.
            </p>
          </div>

          <div className="space-y-6" data-id="element-68">
            <h3 className="text-xl font-serif text-burgundy-800 text-center" data-id="element-69">
              Tu Protocolo de Recuperación está listo
            </h3>

            <Button fullWidth showArrow className="text-lg py-5 shadow-premium-hover" onClick={() => window.open('https://go.hotmart.com/E101139832A?ap=2e42', '_blank')} data-id="element-70">
              Ver protocolo de Yoga Facial para mujeres mayores de 40 años
            </Button>

            <p className="text-center text-sm text-slate-500 mt-4" data-id="element-71">
              * El protocolo incluye ejercicios específicos para tu nivel de
              atrofia.
            </p>
          </div>
        </div>
      </div>
    </motion.div>;
}